import {DATASETS,HISTORY,HISTORY_HEADERS,APP_INFO,WELCOME,SCHEMA_VERSION,APP_NAME,datasetRow,decodeRows,assertUploadAllowed,validateSnapshotCounts,prepareSyncSnapshot,validateSyncedRules,stableData,recordKey,applyRecordDelta,validateRecordLinks,validateRevisionChain,TOMBSTONE_FIELD,FINANCIAL_ENGINE_VERSION} from './workspaceSchema.js';
import {APP_ID} from './bpaAuth.js';
const API='https://sheets.googleapis.com/v4/spreadsheets';
const SCOPE='https://www.googleapis.com/auth/spreadsheets';
let googleToken='',expiresAt=0,scopeKey='',googleAuthGeneration=0;
const uploadsInFlight=new Set();
// A customer-scoped timestamp suppresses repeat quota errors across same-origin
// tabs and refreshes. No tokens, personal data or sheet contents are cached.
const READ_QUOTA_COOLDOWN_MS=65000;
const quotaKey=()=>scopeKey?'bpa-expense-sheets-read-cooldown:'+scopeKey:'';
function quotaRemaining(){
 try{return Math.max(0,Number(localStorage.getItem(quotaKey()))-Date.now()||0)}
 catch{return 0}
}
function rememberQuotaLimit(response){
 const retryAfter=Number(response?.headers?.get?.('Retry-After'));
 const until=Date.now()+Math.max(READ_QUOTA_COOLDOWN_MS,
  Number.isFinite(retryAfter)&&retryAfter>0?retryAfter*1000:0);
 try{if(scopeKey)localStorage.setItem(quotaKey(),String(until))}catch{}
 return until-Date.now();
}
function readQuotaError(milliseconds){
 const seconds=Math.max(1,Math.ceil(milliseconds/1000));
 const error=new Error('Google Sheets is temporarily limiting read requests for this Google account. Retry Check Sheet in about '+
  seconds+' seconds. Your local financial records and linked workbook are unchanged; do not create a second workbook.');
 error.status=429;error.code='GOOGLE_READ_QUOTA';return error;
}
function isReadQuotaFailure(response,json){
 if(response.status===429)return true;
 if(response.status!==403)return false;
 const detail=(json?.error?.errors||[]).map(item=>item?.reason||'').join(' ')+
  ' '+(json?.error?.details||[]).map(item=>item?.reason||'').join(' ');
 return /userRateLimitExceeded|rateLimitExceeded|quotaExceeded|read requests|requests per minute/i.test(
  detail+' '+String(json?.error?.message||''));
}
// Keep only the *short-lived* Google bearer token in per-tab session storage.
// Never use localStorage, never store refresh tokens, and never reuse across
// different verified BPA customers, OAuth clients, or browser origins.
const GOOGLE_SESSION_KEY='bpa-expense-google-tab-auth:v1';
function clearCachedGoogleSession(){
 try{globalThis.sessionStorage?.removeItem(GOOGLE_SESSION_KEY)}catch{}
}
function cacheGoogleSession(){
 try{globalThis.sessionStorage?.setItem(GOOGLE_SESSION_KEY,JSON.stringify({
   owner:scopeKey,clientId:import.meta.env.VITE_GOOGLE_CLIENT_ID,
   origin:globalThis.location?.origin||'',token:googleToken,expiresAt
 }))}catch{ /* Storage can be disabled; in-memory authorization still works. */ }
}
function restoreGoogleSession(){
 if(!scopeKey)return false;
 try{
   const raw=globalThis.sessionStorage?.getItem(GOOGLE_SESSION_KEY);
   if(!raw)return false;
   const saved=JSON.parse(raw);
   if(saved.owner!==scopeKey||
      saved.clientId!==import.meta.env.VITE_GOOGLE_CLIENT_ID||
      saved.origin!==(globalThis.location?.origin||'')||
      typeof saved.token!=='string'||!saved.token||
      !Number.isFinite(saved.expiresAt)||Date.now()+60000>=saved.expiresAt){
     clearCachedGoogleSession();return false;
   }
   googleToken=saved.token;expiresAt=saved.expiresAt;
   return true;
 }catch{clearCachedGoogleSession();return false}
}
const STORE='bpa-expense-google-sheet:';
const CURRENT='bpa-expense-sync-base:';
const MANIFEST='bpa-expense-sync-manifest:';
const CHECKPOINT_EVERY=10;
export const setGoogleScope=(userId)=>{
 if(!userId||!APP_ID)throw new Error('Sign in with an entitled BPA account first.');
 const next=APP_ID+':'+userId;
 if(next===scopeKey)return;
 googleToken='';expiresAt=0;scopeKey=next;googleAuthGeneration++;
 // Called only after a verified BPA customer session/entitlement is adopted.
 // Mismatching/expired sessions are discarded and cannot cross customers.
 restoreGoogleSession();
};
export const getSheetId=()=>scopeKey?localStorage.getItem(STORE+scopeKey)||'':'';
export const rememberSheetId=id=>{if(!scopeKey)throw new Error('BPA account required');if(id)localStorage.setItem(STORE+scopeKey,id);else localStorage.removeItem(STORE+scopeKey)};
const baselineKey=id=>CURRENT+scopeKey+':'+id;
const manifestKey=id=>MANIFEST+scopeKey+':'+id;
export const getBaseline=id=>scopeKey?localStorage.getItem(baselineKey(id))||'':null;
const setBaseline=(id,revision)=>{if(revision)localStorage.setItem(baselineKey(id),revision);else localStorage.removeItem(baselineKey(id))};
export function getLocalManifest(id){
 if(!scopeKey)return null;
 const raw=localStorage.getItem(manifestKey(id));
 if(!raw)return null;
 try{const v=JSON.parse(raw);return v.revision===getBaseline(id)?v:null}catch{return null}
}
const persistManifest=(id,manifest)=>{
 try{localStorage.setItem(manifestKey(id),JSON.stringify(manifest))}
 catch(error){localStorage.removeItem(manifestKey(id));console.warn('Sync manifest unavailable; next Sync Now will write a full checkpoint.',error)}
};
export const isGoogleConfigured=()=>Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);
export const isGoogleAuthorized=()=>{
 if(googleToken&&Date.now()+30000<expiresAt)return true;
 if(googleToken){googleToken='';expiresAt=0;clearCachedGoogleSession()}
 return false;
};
function loadScript(){return new Promise((resolve,reject)=>{if(window.google?.accounts?.oauth2)return resolve();
 const existing=document.querySelector('script[data-google-identity]');if(existing){existing.addEventListener('load',resolve,{once:true});existing.addEventListener('error',reject,{once:true});return;}
 const el=document.createElement('script');el.dataset.googleIdentity='true';el.src='https://accounts.google.com/gsi/client';el.async=true;el.onload=resolve;el.onerror=()=>reject(new Error('Google sign-in library could not load'));document.head.appendChild(el);});
}
export async function authorizeGoogle(){
 const clientId=import.meta.env.VITE_GOOGLE_CLIENT_ID;
 if(!clientId)throw new Error('BPA Solutions must configure VITE_GOOGLE_CLIENT_ID and authorise this website origin in Google Cloud.');
 if(!scopeKey)throw new Error('Verify your BPA customer account before authorising Google.');
 const ownerAtRequest=scopeKey,generationAtRequest=googleAuthGeneration;
 await loadScript();
 return new Promise((resolve,reject)=>{
   const client=window.google.accounts.oauth2.initTokenClient({
     client_id:clientId,scope:SCOPE,
     callback:response=>{
       if(scopeKey!==ownerAtRequest||googleAuthGeneration!==generationAtRequest)
         return reject(new Error('BPA login changed or signed out during Google authorisation. Please sign in again.'));
       if(!response?.access_token)return reject(new Error(response?.error_description||'Google authorisation was cancelled'));
       googleToken=response.access_token;
       expiresAt=Date.now()+Math.min(3600,Math.max(60,Number(response.expires_in)||3600))*1000;
       cacheGoogleSession();
       resolve();
     },
     error_callback:()=>reject(new Error('Google sign-in did not complete. Verify the exact website origin in Google Cloud and allow the popup.'))
   });
   // Reconnecting a previously linked customer-owned workbook should not
   // force the account chooser on every token renewal.
   client.requestAccessToken({prompt:getSheetId()?'':'select_account'});
 });
}
export function clearGoogleAuth(){
 googleToken='';expiresAt=0;googleAuthGeneration++;
 clearCachedGoogleSession();
}
async function request(path,options={}){
 if(!isGoogleAuthorized())throw new Error('Google permission has expired. Choose Reconnect Google; your local financial data remains available.');
 // Read-only checks must not leave the interface waiting forever when
 // Google/network stalls. Never abort an in-flight write: append results can
 // be ambiguous and must not be retried blindly.
 const isRead=(!options.method||options.method==='GET');
 if(isRead&&quotaRemaining()>0)throw readQuotaError(quotaRemaining());
 const controller=isRead&&typeof AbortController!=='undefined'?new AbortController():null;
 const timer=controller?setTimeout(()=>controller.abort(),30000):null;
 let response;
 try{
   response=await fetch(API+path,{...options,...(controller?{signal:controller.signal}:{}),
     headers:{Authorization:'Bearer '+googleToken,...(options.body?{'Content-Type':'application/json'}:{}),...options.headers}});
 }catch(error){
   if(controller?.signal.aborted)throw new Error('Google Sheets took too long to respond. Check your connection and choose Check Sheet again. No financial records were modified by this read.');
   throw error;
 }finally{if(timer!==null)clearTimeout(timer)}
 const json=await response.json().catch(()=>({}));if(response.status===401){clearGoogleAuth();throw new Error('Google permission has expired. Reconnect Google.')}
 if(!response.ok){
  if(isRead&&isReadQuotaFailure(response,json))throw readQuotaError(rememberQuotaLimit(response));
  const e=new Error(json.error?.message||'Google Sheets request failed ('+response.status+')');e.status=response.status;throw e;
 }
 return json;
}
export function extractSpreadsheetId(value){const input=String(value||'').trim();const match=input.match(/\/spreadsheets\/d\/([A-Za-z0-9_-]+)/);return match?match[1]:/^[A-Za-z0-9_-]{20,120}$/.test(input)?input:''}
const range=(tab,cells)=>encodeURIComponent("'"+tab+"'!"+cells);
const rgb=hex=>{let h=hex.replace('#','');return {red:parseInt(h.slice(0,2),16)/255,green:parseInt(h.slice(2,4),16)/255,blue:parseInt(h.slice(4,6),16)/255}};
const NAVY=rgb('#032D60'),BLUE=rgb('#0176D3'),ORANGE=rgb('#FE9339'),WHITE=rgb('#FFFFFF');
// Google Sheets INSERT_ROWS can copy the previous (navy header) row's styling.
// Apply readable formatting explicitly to all data rows, never the frozen header.
const bodyFormat=()=>({backgroundColor:WHITE,textFormat:{foregroundColor:NAVY,bold:false},verticalAlignment:'MIDDLE',wrapStrategy:'CLIP'});
const bodyFormatFields='userEnteredFormat.backgroundColor,userEnteredFormat.textFormat.foregroundColor,userEnteredFormat.textFormat.bold,userEnteredFormat.verticalAlignment,userEnteredFormat.wrapStrategy';
const bodyFormatRequest=(sheetId,columnCount)=>({repeatCell:{range:{sheetId,startRowIndex:1,startColumnIndex:0,endColumnIndex:columnCount},cell:{userEnteredFormat:bodyFormat()},fields:bodyFormatFields}});
export async function formatWorkbookRows(id,{alreadyVerified=false}={}){
 // Formatting only. No values, financial revisions or workbook references change.
 // upload() has just verified the complete workbook; avoid repeating 10+
 // Google header reads on every automatically backed-up transaction.
 const workbook=alreadyVerified?{id:extractSpreadsheetId(id)}:await verifyWorkbook(id);
 const meta=await metadata(workbook.id);
 const headers=new Map(expectedHeaders());
 const requests=(meta.sheets||[]).filter(sheet=>headers.has(sheet.properties.title))
   .map(sheet=>bodyFormatRequest(sheet.properties.sheetId,headers.get(sheet.properties.title).length));
 if(requests.length)await request('/'+encodeURIComponent(workbook.id)+':batchUpdate',
   {method:'POST',body:JSON.stringify({requests})});
 return {formattedTabs:requests.length};
}
async function batchWrite(id,data){await request('/'+encodeURIComponent(id)+'/values:batchUpdate',{method:'POST',body:JSON.stringify({valueInputOption:'RAW',data})})}
async function values(id,tab,cells='A1:Q'){let r=await request('/'+encodeURIComponent(id)+'/values/'+range(tab,cells));return r.values||[]}
// Sheets values:batchGet charges one read for multiple ranges. Require a complete
// response in requested order; never silently accept missing financial records.
async function batchValues(id,ranges){
 if(!ranges.length)return [];
 const query=new URLSearchParams();
 for(const [tab,cells] of ranges)query.append('ranges',"'"+tab.replace(/'/g,"''")+"'!"+cells);
 const response=await request('/'+encodeURIComponent(id)+'/values:batchGet?'+query.toString());
 if(!Array.isArray(response.valueRanges)||response.valueRanges.length!==ranges.length)
  throw new Error('Google Sheets returned an incomplete batch read. No financial records were changed.');
 return response.valueRanges.map(item=>{
  if(!item||(item.values!==undefined&&!Array.isArray(item.values)))
   throw new Error('Google Sheets returned malformed worksheet values. No financial records were changed.');
  return item.values||[];
 });
}
async function metadata(id){return request('/'+encodeURIComponent(id)+'?fields=spreadsheetId,properties(title),sheets(properties(sheetId,title,gridProperties))')}
const tabs=()=>[WELCOME,...Object.values(DATASETS).map(x=>x.tab),HISTORY,APP_INFO];
const expectedHeaders=()=>[...Object.values(DATASETS).map(x=>[x.tab,x.headers]),[HISTORY,HISTORY_HEADERS]];
export async function createBrandedWorkbook(onProgress=()=>{}){
 const creationScope=scopeKey;
 if(!creationScope)throw new Error('A BPA customer must be signed in before creating a Google workbook.');
 if(!APP_ID)throw new Error('Configure the permanent VITE_BPA_APP_ID before creating workbooks.');
 const titles=tabs();
 onProgress('creating');
 const created=await request('',{method:'POST',body:JSON.stringify({properties:{title:'BPA Expense Manager Pro Database',locale:'en_AU',timeZone:'Australia/Sydney'},sheets:titles.map(title=>({properties:{title,...(title===APP_INFO?{hidden:true}:{}),gridProperties:{hideGridlines:true}}}))})});
 const id=created.spreadsheetId;if(!id)throw new Error('Google did not return a Sheet ID');
 if(scopeKey!==creationScope)throw new Error('BPA account changed during workbook creation. Review Google Drive before starting another setup.');
 // Save before formatting. If setup fails, never create a second Sheet automatically.
 rememberSheetId(id);
 onProgress('formatting');
 try {
  const now=new Date().toISOString();
  const info=[['field','value'],['app_id',APP_ID],['app_name',APP_NAME],['schema_version',SCHEMA_VERSION],['created_at_utc',now],['developer','BPA Solutions'],['support_url','https://www.bpasolutions.au'],['storage_provider','Google Sheets'],['financial_engine_version',FINANCIAL_ENGINE_VERSION]];
  const data=expectedHeaders().map(([title,headers])=>({range:"'"+title+"'!A1",values:[headers]}));
  data.push({range:"'"+WELCOME+"'!A1",values:[['BPA SOLUTIONS | EXPENSE MANAGER PRO']]},{range:"'"+WELCOME+"'!A3",values:[['YOUR PRIVATE FINANCIAL WORKSPACE']]},{range:"'"+WELCOME+"'!A5",values:[['Manage income, transactions, budgets, people and recurring payments from the Expense Manager Pro app.']]},{range:"'"+WELCOME+"'!A7",values:[['Developed by','BPA Solutions'],['Support','https://www.bpasolutions.au'],['Created',now],['Important','Do not rename tabs, change headings or delete SyncHistory.'],['Sync','Changes back up automatically while Google permission is active; Sync Now is also available.'],['Recovery','Restore carefully from a verified revision in Settings.']]},{range:"'"+APP_INFO+"'!A1",values:info});
  await batchWrite(id,data);
  const req=[];
  for(const sheet of created.sheets||[]){
   const p=sheet.properties,head=expectedHeaders().find(([name])=>name===p.title)?.[1];
   req.push({updateSheetProperties:{properties:{sheetId:p.sheetId,gridProperties:{hideGridlines:true,...(head?{frozenRowCount:1}:{})},tabColorStyle:{rgbColor:p.title===WELCOME?ORANGE:BLUE}},fields:'gridProperties.hideGridlines'+(head?',gridProperties.frozenRowCount':'')+',tabColorStyle'}});
   if(head){
    req.push({repeatCell:{range:{sheetId:p.sheetId,startRowIndex:0,endRowIndex:1,startColumnIndex:0,endColumnIndex:head.length},cell:{userEnteredFormat:{backgroundColor:NAVY,textFormat:{bold:true,foregroundColor:rgb('#FFFFFF')}}},fields:'userEnteredFormat.backgroundColor,userEnteredFormat.textFormat'}});
    req.push(bodyFormatRequest(p.sheetId,head.length));
    req.push({updateDimensionProperties:{range:{sheetId:p.sheetId,dimension:'COLUMNS',startIndex:0,endIndex:head.length},properties:{pixelSize:175},fields:'pixelSize'}});
   }
  }
  await request('/'+encodeURIComponent(id)+':batchUpdate',{method:'POST',body:JSON.stringify({requests:req})});
  onProgress('verifying');
  await verifyWorkbook(id);onProgress('structured');return {id,title:created.properties?.title||'BPA Expense Manager Pro Database'};
 }catch(err){throw new Error('Google Sheet '+id+' was created, but setup needs repair: '+err.message+'. Reconnect the same Sheet; do not create another.')}
}
export async function verifyWorkbook(value){
 const id=extractSpreadsheetId(value);if(!id)throw new Error('Enter a valid Google Sheet URL or case-sensitive ID.');
 const book=await metadata(id);if(book.spreadsheetId!==id)throw new Error('Google returned another workbook.');
 const names=new Set((book.sheets||[]).map(s=>s.properties?.title));
 for(const name of tabs())if(!names.has(name))throw new Error('This is not a complete BPA Expense Manager Pro workbook. Existing data was not changed. Missing '+name);
 const specs=expectedHeaders();
 const reads=await batchValues(id,[[APP_INFO,'A1:B30'],...specs.map(([title])=>[title,'A1:Q1'])]);
 const info=new Map(reads[0].slice(1).map(row=>[row[0],row[1]]));
 if(info.get('app_id')!==APP_ID||info.get('schema_version')!==SCHEMA_VERSION)throw new Error('This Sheet is for another app or schema version. No data was changed.');
 for(let i=0;i<specs.length;i++){
  const [title,headers]=specs[i];
  const actual=reads[i+1][0]||[];
  if(JSON.stringify(actual.slice(0,headers.length))!==JSON.stringify(headers))throw new Error('Existing '+title+' headers differ. No data was changed.');
 }
 return {id,title:book.properties?.title||APP_NAME};
}
// Legacy four-column history is deliberately refused: changing worksheet format
// without a verified migration could accidentally mix incompatible revisions.
const parseHistory=(rows)=>rows.filter(row=>row?.[0]).map(row=>{
 if(row.length<8)throw new Error('Legacy SyncHistory format requires a reviewed migration; existing data was not changed.');
 return {revision:row[0],date:row[1],counts:JSON.parse(row[2]||'{}'),
  checksum:row[3],parentRevision:row[4]||'',mode:row[5],
  engineVersion:row[6],changedCounts:JSON.parse(row[7]||'{}')};
});
export async function readHistory(id){
 const rows=await values(id,HISTORY,'A2:H');
 const history=parseHistory(rows);
 validateRevisionChain(history);
 for(let i=0;i<history.length;i++){
  const h=history[i];
  if(h.mode!=='full'&&h.mode!=='delta')throw new Error('Unknown sync revision mode');
  if(i===0&&h.mode!=='full')throw new Error('Sync history has no initial full checkpoint');
  if(!h.checksum||!h.counts||!h.changedCounts)throw new Error('Incomplete financial sync history');
 }
 return history;
}
export async function latestRevision(id){
 const history=await readHistory(id);
 return history.at(-1)||null;
}
async function checksum(data){
 const canonical=stableData(data);
 const raw=new TextEncoder().encode(JSON.stringify(canonical));
 const hash=await crypto.subtle.digest('SHA-256',raw);
 return Array.from(new Uint8Array(hash),x=>x.toString(16).padStart(2,'0')).join('');
}
async function fingerprint(item){
 const hash=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(JSON.stringify(item)));
 return Array.from(new Uint8Array(hash),v=>v.toString(16).padStart(2,'0')).join('');
}
async function buildManifest(revision,data){
 const manifest={revision,hashes:{}};
 for(const [dataset,records] of Object.entries(stableData(data))){
  const output={};
  for(const record of records)output[recordKey(dataset,record)]=await fingerprint(record);
  manifest.hashes[dataset]=output;
 }
 return manifest;
}
async function changedFromManifest(data,manifest){
 const delta={},full=stableData(data);
 for(const [dataset,records] of Object.entries(full)){
  const old=manifest.hashes?.[dataset]||{};
  const now={};const modified=[];
  for(const record of records){
   const id=recordKey(dataset,record);
   now[id]=await fingerprint(record);
   if(old[id]!==now[id])modified.push(record);
  }
  for(const id of Object.keys(old))if(!Object.hasOwn(now,id))
   modified.push(dataset==='settings'?{key:id,[TOMBSTONE_FIELD]:true}:{id,[TOMBSTONE_FIELD]:true});
  delta[dataset]=modified;
 }
 return delta;
}
const countRecords=dataset=>Object.fromEntries(Object.keys(DATASETS).map(key=>[key,dataset[key].length]));
function deltaCount(dataset){return Object.values(dataset).reduce((sum,rows)=>sum+rows.length,0)}
async function appendDatasetRows(id,data,revision){
 for(const [dataset,spec] of Object.entries(DATASETS)){
  const rows=data[dataset].map(item=>datasetRow(dataset,revision,item));
  if(!rows.length)continue;
  // Google recommends request payloads <= 2 MB. Leave room for JSON framing.
  // Do not blindly retry ambiguous append failures: they are not idempotent.
  let batch=[],bytes=0;
  async function flush(){
   if(!batch.length)return;
   const appended=await request('/'+encodeURIComponent(id)+'/values/'+range(spec.tab,'A1')+':append?valueInputOption=RAW&insertDataOption=INSERT_ROWS',
    {method:'POST',body:JSON.stringify({values:batch})});
   if(appended.updates?.updatedRows!=null && Number(appended.updates.updatedRows)!==batch.length)
    throw new Error('Google Sheets did not acknowledge all '+spec.tab+' records. The revision was not marked complete.');
   batch=[];bytes=0;
  }
  for(const row of rows){
   const cell=String(row.at(-1)||'');
   if(cell.length>45000)throw new Error('A '+dataset+' record exceeds the safe Google Sheets cell size. No sync revision was committed.');
   const size=new TextEncoder().encode(JSON.stringify(row)).length+64;
   if(batch.length&&(bytes+size>1800000||batch.length>=300))await flush();
   batch.push(row);bytes+=size;
  }
  await flush();
 }
}
async function upload(id,backup,onlyIfEmpty=false,onProgress=()=>{}){
 const uploadScope=scopeKey;
 if(!uploadScope)throw new Error('Sign in to the correct BPA account before uploading.');
 await verifyWorkbook(id);
 if(scopeKey!==uploadScope)throw new Error('BPA customer changed during Google verification; nothing was uploaded.');
 const history=await readHistory(id);
 const head=history.at(-1)||null;
 if(onlyIfEmpty&&head)return {created:false,...head,requiresReconciliation:true};
 const baseline=getBaseline(id)||'';
 assertUploadAllowed(head?.revision||'',baseline);
 const full=validateRecordLinks(stableData(prepareSyncSnapshot(backup)));
 validateSyncedRules(full);
 const prevManifest=getLocalManifest(id);
 // A missing or stale manifest is a safe full checkpoint, never an untrusted delta.
 const deltaMode=!onlyIfEmpty&&Boolean(head&&prevManifest)&&
  history.length%CHECKPOINT_EVERY!==0;
 const rowsToWrite=deltaMode?await changedFromManifest(full,prevManifest):full;
 const changedCounts=countRecords(rowsToWrite),counts=countRecords(full);
 const revision=Date.now().toString(36)+'-'+crypto.randomUUID();
 const writtenAt=new Date().toISOString(),digest=await checksum(full);
 // Do not append a no-op change that would grow Google Sheets needlessly.
 if(deltaMode&&deltaCount(rowsToWrite)===0)return {revision:head.revision,date:head.date,
  counts:head.counts,changedCounts,unchanged:true};
 // Optimistic remote check catches many stale writes. This is NOT an atomic
 // lease, so a concurrent fork is detectable and requires manual repair.
 if((await latestRevision(id))?.revision!==(head?.revision||undefined))
  throw new Error('Google Sheet changed before upload. No data was written.');
 onProgress('uploading');
 await appendDatasetRows(id,rowsToWrite,revision);
 onProgress('committing');
 if((await latestRevision(id))?.revision!==(head?.revision||undefined))
  throw new Error('Concurrent Google edit detected before commit; data was not marked complete. Review SyncHistory before retrying.');
 const mode=deltaMode?'delta':'full';
 await request('/'+encodeURIComponent(id)+'/values/'+range(HISTORY,'A1')+':append?valueInputOption=RAW&insertDataOption=INSERT_ROWS',
  {method:'POST',body:JSON.stringify({values:[[revision,writtenAt,JSON.stringify(counts),digest,
   head?.revision||'',mode,FINANCIAL_ENGINE_VERSION,JSON.stringify(changedCounts)]]})});
 const confirmed=await latestRevision(id);
 if(confirmed?.revision!==revision||confirmed.checksum!==digest)
  throw new Error('Google did not confirm the committed revision. Verify Sheet before retrying.');
 // A new workspace or periodic complete checkpoint must actually round-trip
 // all financial records before the app can call the sync verified.
 if(mode==='full'){
  onProgress('readback');
  const verified=await downloadSnapshot(id);
  const retrieved={...verified.data,categoryRules:verified.categoryRules};
  if(await checksum(retrieved)!==digest)
   throw new Error('Full Google sync read-back did not match local financial data. Review Sheet before retrying.');
 }
 // Never write customer A's baseline/manifest into customer B's local scope
 // if the account switched while a Google network request was in flight.
 if(scopeKey!==uploadScope)throw new Error('Customer account changed during Google backup. Remote revision may have completed; check Sheet before retrying.');
 // Save manifest before baseline. If either fails, next upload uses a safe full snapshot.
 const manifest=await buildManifest(revision,full);
 persistManifest(id,manifest);
 setBaseline(id,revision);
 onProgress('backup-verified');
 let formattingWarning='';
 try{await formatWorkbookRows(id,{alreadyVerified:true})}
 catch(e){formattingWarning='Financial data was saved, but row styling needs repair: '+e.message;
   console.warn('BPA Google row-formatting is incomplete',e);}
 return {revision,date:writtenAt,counts,changedCounts,mode,formattingWarning};
}
async function exclusiveUpload(id,work){
 if(uploadsInFlight.has(id)){
   const error=new Error('Another Google upload is already running in this tab. Your local changes are safe.');
   error.code='SYNC_BUSY';throw error;
 }
 uploadsInFlight.add(id);
 try{
   const locks=typeof navigator!=='undefined'?navigator.locks:null;
   if(locks?.request){
     return await locks.request('bpa-expense-sync:'+id,{mode:'exclusive',ifAvailable:true},async lock=>{
       if(!lock){const error=new Error('Another tab is uploading this workbook. Try again after it finishes.');
         error.code='SYNC_BUSY';throw error;}
       return work();
     });
   }
   return await work();
 }finally{uploadsInFlight.delete(id)}
}
export const uploadSnapshot=(id,backup,onProgress)=>exclusiveUpload(id,()=>upload(id,backup,false,onProgress));
export async function downloadSnapshot(id){
 await verifyWorkbook(id);
 const history=await readHistory(id);
 if(!history.length)throw new Error('There is no completed Sheet backup yet.');
 // Reconstruct only from the newest verified full checkpoint. Older history
 // remains in Google Sheets for audit, but doesn't slow down each restore.
 const checkpointIndex=history.map(entry=>entry.mode).lastIndexOf('full');
 if(checkpointIndex<0)throw new Error('A complete recovery checkpoint is missing');
 const replay=history.slice(checkpointIndex);
 const records={};
 const specs=Object.entries(DATASETS);
 const readRows=await batchValues(id,specs.map(([,spec])=>[spec.tab,'A2:Q']));
 for(let datasetIndex=0;datasetIndex<specs.length;datasetIndex++){
  const [dataset]=specs[datasetIndex];
  const rows=readRows[datasetIndex];
  const committed=new Set(replay.map(item=>item.revision));
  const grouped=new Map();
  for(const row of rows){
   if(!committed.has(row[0]))continue;
   if(!grouped.has(row[0]))grouped.set(row[0],[]);
   grouped.get(row[0]).push(row);
  }
  records[dataset]=new Map();
  for(const item of replay){
   const decoded=decodeRows(dataset,grouped.get(item.revision)||[],item.revision);
   records[dataset].set(item.revision,decoded);
  }
 }
 let current=null;
 for(const entry of replay){
  const group=Object.fromEntries(Object.keys(DATASETS).map(dataset=>[
   dataset,records[dataset].get(entry.revision)||[]
  ]));
  if(entry.mode==='full'){
   // Complete checkpoints have no deletion markers.
   current=validateRecordLinks(stableData(group));
  }else{
   if(!current)throw new Error('Missing initial full sync checkpoint.');
   current=applyRecordDelta(current,group);
  }
  validateSyncedRules(current);
  validateSnapshotCounts(current,entry.counts);
  if(await checksum(current)!==entry.checksum)
   throw new Error('Google Sheet checksum failed for revision '+entry.revision+'. Local data was not changed.');
 }
 const head=history.at(-1);
 const {categoryRules,...databaseData}=current;
 return {version:'2.0.0',exportDate:head.date,data:databaseData,categoryRules,
  revision:head.revision,counts:head.counts};
}
// On initial connection a complete local snapshot is saved immediately.
// A previously-populated remote workbook must NEVER be silently overwritten,
// even if this browser has never synced before.
export async function initialiseSheetFromLocal(id,backup,onProgress=()=>{}){
 onProgress('checking-history');
 await verifyWorkbook(id);
 const latest=await latestRevision(id);
 if(!latest){
  // A failed earlier upload may leave uncommitted rows. Do not silently layer
  // a new revision over unknown customer data or a manually populated Sheet.
  const specs=Object.values(DATASETS);
  const existingRows=await batchValues(id,specs.map(spec=>[spec.tab,'A2:B']));
  for(let i=0;i<specs.length;i++){
   if(existingRows[i].length)throw new Error(
     'The '+specs[i].tab+' worksheet contains data but no completed SyncHistory revision. The existing rows were not modified. Review or repair this workbook before retrying the initial upload.');
  }
 }
 if(latest){
  return {created:false,revision:latest.revision,date:latest.date,counts:latest.counts,
   requiresReconciliation:true};
 }
 const saved=await exclusiveUpload(id,()=>upload(id,backup,true,onProgress));
 return {created:true,...saved,requiresReconciliation:false};
}
// Recover an absent per-device baseline ONLY if the user's complete local
// snapshot has the exact SHA-256 recorded in the remote committed revision.
// This never imports data, uploads data, or assumes which device is newer.
export async function reconcileIdenticalLocalBackup(id,backup){
 const head=await latestRevision(id);
 if(!head)return {matched:false,reason:'There is no completed Google backup to compare.'};
 const local=validateRecordLinks(stableData(prepareSyncSnapshot(backup)));
 validateSyncedRules(local);
 const digest=await checksum(local);
 if(digest!==head.checksum)return {matched:false,head,
   reason:'The local financial records and the last Google backup differ. No data was changed. Review or restore the backup before uploading.'};
 const confirmed=await latestRevision(id);
 if(confirmed?.revision!==head.revision||confirmed?.checksum!==head.checksum)
   throw new Error('Google revision changed during verification. Retry Check Sheet; no financial data was changed.');
 await markRestored(id,head.revision,backup);
 return {matched:true,head:confirmed};
}
// Recover an existing completed Google workbook without silently overwriting it.
// Exact matches resume immediately. Strictly additional local records are safe
// to upload as a new revision; changed/deleted remote records are NOT auto-merged.
// Progress callbacks report real phases; no financial data is changed here.
export async function reconcileSafeLocalChanges(id,backup,onProgress=()=>{}){
 const checkedScope=scopeKey;
 if(!checkedScope)throw new Error('Sign in to your BPA customer account first.');
 onProgress('history','Reading the latest completed Google revision');
 const head=await latestRevision(id);
 if(!head)return {kind:'empty',reason:'There is no completed Google backup yet. No data was changed.'};
 onProgress('download','Downloading and verifying the saved Google backup');
 const remoteBackup=await downloadSnapshot(id); // Checks every record count and SHA-256.
 if(remoteBackup.revision!==head.revision)
   throw new Error('Google changed during the comparison. Retry Check Sheet.');
 onProgress('compare','Comparing local transactions, categories and settings with Google');
 const remote=validateRecordLinks(stableData(prepareSyncSnapshot(remoteBackup)));
 const local=validateRecordLinks(stableData(prepareSyncSnapshot(backup)));
 validateSyncedRules(remote);validateSyncedRules(local);
 const additions={};
 let totalAdditions=0;
 for(const key of Object.keys(DATASETS)){
   const existing=new Map(remote[key].map(item=>[recordKey(key,item),item]));
   const current=new Map(local[key].map(item=>[recordKey(key,item),item]));
   if(existing.size!==remote[key].length||current.size!==local[key].length)
     return {kind:'conflict',head,reason:'Duplicate record identifiers in '+key+'. Nothing was changed.'};
   for(const [id,row] of existing){
     if(!current.has(id))return {kind:'conflict',head,reason:'Google contains a '+key+' record missing on this device. Nothing was changed.'};
     if(JSON.stringify(current.get(id))!==JSON.stringify(row))
       return {kind:'conflict',head,reason:'The '+key+' record '+id+' differs from Google. Neither copy was overwritten.'};
   }
   additions[key]=current.size-existing.size;
   totalAdditions+=additions[key];
 }
 onProgress('confirm','Checking that the Google revision has not changed');
 const again=await latestRevision(id);
 if(again?.revision!==head.revision||again?.checksum!==head.checksum)
   throw new Error('Google was updated during comparison. Retry Check Sheet without uploading.');
 if(scopeKey!==checkedScope)throw new Error('BPA customer changed during verification. Nothing was linked.');
 onProgress('baseline','Safely linking this device to the verified Google revision');
 // Use the REMOTE data for the manifest: upcoming uploads should contain
 // local additions, not treat them as if they were already backed up.
 await markRestored(id,head.revision,remoteBackup);
 return {kind:totalAdditions?'additions':'identical',head,additions,totalAdditions};
}
export async function markRestored(id,revision,backup){
 if(backup){const full=stableData(prepareSyncSnapshot(backup));persistManifest(id,await buildManifest(revision,full));}
 else localStorage.removeItem(manifestKey(id));
 setBaseline(id,revision);
}